import { useContext, useEffect, useState, useRef } from "react";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import CartService from "../services/CartService";
import StoreService from "../services/StoreService";
import { cleanUserSession } from "../state/actions";
import { STORE_ID_DEFAULT } from "../constants/businessValues";
import {
  userDummySelector,
  userLoggedSelector,
} from "../state/selectors/sessionSelectors";
import { searchPaymentActive } from "../utils/searchPayment";
import { errorConection } from "../utils";
import { stl } from "../styles/base";
import LoadingContext from "../context/LoadingProvider";
import FastCard from "../components/card/FastCard";
import MessageModal from "../components/MessageModal";
import PaymentService from "../services/PaymentService";

export const initialStateCard = [];

const PaymentChoose = () => {
  const location = useLocation();
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [data, setData] = useState<string[]>([]);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const { setLoadingValue } = useContext(LoadingContext);
  const userLogged = useSelector(userLoggedSelector);
  const userDummy = useSelector(userDummySelector);
  const emailLogin = userLogged?.login || userDummy?.login;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let engineInterval: any;

  const locationRedirectUrl = location.state?.redirectUrl;

  useEffect(() => {
    if (locationRedirectUrl) {
      localStorage.setItem("redirectUrl", locationRedirectUrl);
      setRedirectUrl(locationRedirectUrl);
    } else {
      const savedUrl = localStorage.getItem("redirectUrl");
      if (savedUrl) {
        setRedirectUrl(savedUrl);
      }
    }
  }, [locationRedirectUrl]);

  const startInterval = () => {
    engineInterval = setInterval(() => {
      checkAccessStatus();
    }, 2000);
  };

  const stopInterval = () => {
    clearInterval(engineInterval);
  };

  const getPaymentsList = async () => {
    setLoadingValue(true);
    PaymentService.getUserPaymentMethods(emailLogin)
      .then((data) => {
        if (data instanceof Error) {
          errorConection("TypeError: Network request failed");
        } else if (data) {
          const paymentActive = searchPaymentActive(data);
          !paymentActive &&
            PaymentService.updateDefaultPayment({
              id: data[0][0],
              login: emailLogin,
              state: "active",
            });
          setData(data);
        }
      })
      .finally(() => setLoadingValue(false));
  };

  const handleCardClick = async (card: any) => {
    setLoadingPayment(true);
    try {
      const res = await PaymentService.confirmPayment(emailLogin, card[0]);
      if (res instanceof Error) {
        toast("Error al realizar el pago");
        setLoadingPayment(false);
      } else if( res.result.payment_status === 1) {
        stopInterval(); // Detener el intervalo de monitoreo
        }
    } catch (error) {
      toast("Fallo inesperado", { style: stl.errorStyleToast });
      setLoadingPayment(false);
    }
  };

  useEffect(() => {
    emailLogin ? getPaymentsList() : navigate("");
    startInterval();
  }, []);

  const messagesByCodeDoor = [
    {
      code: 100,
      title: "Pago realizado. Pronto recibirás la factura en tu correo.",
      description: ["El pago fue procesado con éxito"],
      action: () => {
        navigate("/summary");
      },
    },
    {
      code: 17,
      title: "Falló el pago. Revise su tarjeta o cambie de medio",
      description: [
        "No se pudo procesar el cobro con tu medio de pago predeterminado.",
        "Utilice un medio de pago distinto.",
      ],
      action: () => {
        setLoadingPayment(false);
        // Se queda en esta pantalla
      },
    },
  ];

  const configMsgByCode = (code: number) => {
    const match = messagesByCodeDoor.find((msg) => msg.code === code);
    if (match) {
      toast(match.title);
      match.action();
    }
  };

  const checkAccessStatus = async () => {
    const response = await StoreService.getAccessStatus(
      userLogged.login,
      STORE_ID_DEFAULT
    );
    if (response?.result?.length > 0) {
      const lastStatus = response.result[response.result.length - 1];
      const code = parseInt(lastStatus.code);
      configMsgByCode(code);
    }
  };

  useEffect(() => {
    return () => {
      stopInterval();
    };
  }, []);

  return (
    <>
      <Stack>
        <Box marginBottom={5}>
          <Typography variant="h5" color="white" align="center">
            Selección de Medio de Pago
            <br />
            {userLogged?.name || "Usuari@"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h6" color="white" align="left" marginBottom={2}>
            Podés elegir una tarjeta
          </Typography>
        </Box>
        <Box width={"100%"}>
          {!data.length ? (
            <Typography variant="h6" color="white" align="center" marginBottom={2}>
              No hay tarjetas agregadas
            </Typography>
          ) : (
            data.map((values, index) => (
              <FastCard
                key={index}
                values={values}
                onClickCard={handleCardClick}
              />
            ))
          )}
        </Box>
      </Stack>
      <Stack spacing={2}>
        <Link
          onClick={() =>
            navigate("/payment/new", { state: { from: "/payment/choose" } })
          }
        >
          <Button variant="contained" fullWidth>
            Agregar Tarjeta
          </Button>
        </Link>

        <Box>
          <Typography variant="h6" color="white" align="center" margin={2}>
            ...o abrir tu billetera virtual y escanear el código QR
          </Typography>
        </Box>

        <div style={style.retCartBtn}>
          <Button
            variant="outlined"
            fullWidth
            onClick={async () => {
              try {
                setLoadingValue(true);
                await CartService.postLeaveRetryOrder({
                  userId: emailLogin,
                  storeCode: "2",
                  doorId: "3",
                  code: 11,
                  message: "Retry order payment",
                });
                navigate("/cart");
              } catch (error) {
                toast("Error al devolver productos");
              } finally {
                setLoadingValue(false);
              }
            }}
          >
            Devolver Productos
          </Button>
        </div>
      </Stack>

      <MessageModal
        type="text"
        Icon={() => null}
        title="Procesando pago..."
        text={["Estamos intentando procesar tu pago", "Por favor, espera."]}
        actions={[]} // No mostrar botones
        handleClose={() => {}} // No permite cerrar manualmente
        open={loadingPayment}
      />
    </>
  );
};

const style: any = {
  content: {
    display: "flex",
    flexDirection: "column",
    margin: 18,
  },
  cardBtn: {
    textDecoration: "none",
    boxShadow: "none",
  },
  retCartBtn: {
    marginTop: 20,
  },
};

export default PaymentChoose;
